import { useState } from "react";

import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { interpolateTemplate } from "lib/i18n/dictionary";

import { isJobActive } from "./provider-site";
import { getReadyDesignName } from "./ready-designs";
import type { ProviderSite, ReadyDesign } from "./site.documents";
import { useRestoreSiteVersion } from "./site.hooks";
import { useSiteTranslations } from "./use-site-translations";

// "Версії": each publish, with "Повернути" putting an older one back into the draft.
export function SiteVersions({
  site,
  profileUuid,
  designs,
  run,
}: {
  site: ProviderSite;
  profileUuid: string;
  designs: ReadyDesign[];
  run: (
    action: () => Promise<unknown>,
    errorTitle: string,
    success?: { title: string; description: string }
  ) => Promise<void>;
}) {
  const { t, intlLocale } = useSiteTranslations();
  const [restoreVersion] = useRestoreSiteVersion();
  const [restoringNumber, setRestoringNumber] = useState<number | null>(null);

  if (site.versions.length === 0) {
    return null;
  }

  return (
    <Card data-testid="provider-site-versions">
      <SoftBox p={3}>
        <SoftTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
          {t.versions.title}
        </SoftTypography>
        {site.versions.map((version, index) => {
          const isLive = version.number === site.publishedVersion && site.status === "PUBLISHED";
          const summary =
            version.summary ||
            (version.readyDesign
              ? interpolateTemplate(t.designs.versionSummary, {
                  name: getReadyDesignName(version.readyDesign, designs),
                })
              : null);
          return (
            <SoftBox key={version.number}>
              {index > 0 ? <Divider sx={{ my: 0 }} /> : null}
              <SoftBox display="flex" alignItems="center" gap={2} py={1.5}>
                <SoftBox flexGrow={1}>
                  <SoftTypography variant="button" fontWeight="medium" display="block">
                    {interpolateTemplate(t.versions.version, { number: String(version.number) })}
                    {isLive ? ` · ${t.versions.current}` : ""}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text">
                    {[summary, new Date(version.createdAt).toLocaleDateString(intlLocale)]
                      .filter(Boolean)
                      .join(" · ")}
                  </SoftTypography>
                </SoftBox>
                {version.number !== site.publishedVersion ? (
                  <SoftButton
                    variant="outlined"
                    color="info"
                    size="small"
                    disabled={restoringNumber !== null || isJobActive(site.activeJob)}
                    onClick={() => {
                      setRestoringNumber(version.number);
                      void run(
                        () =>
                          restoreVersion({ variables: { profileUuid, number: version.number } }),
                        t.errors.restoreFailedTitle,
                        {
                          title: interpolateTemplate(t.versions.restored, {
                            number: String(version.number),
                          }),
                          description: t.versions.restoredMessage,
                        }
                      ).finally(() => setRestoringNumber(null));
                    }}
                    data-testid={`provider-site-restore-${version.number}`}
                  >
                    {t.versions.restore}
                  </SoftButton>
                ) : null}
              </SoftBox>
            </SoftBox>
          );
        })}
        <SoftTypography variant="caption" color="text">
          {t.versions.hint}
        </SoftTypography>
      </SoftBox>
    </Card>
  );
}
